/**
 * TimelineContextAdapterOrganism
 *
 * Bridges context execution data to a timeline/Gantt-style rendering.
 * Maps sphere date properties to time-based positioning.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'localization';
import { useContextData } from '../../../hooks/useContextData';
import { useContextView } from '../../../hooks/useContextView';
import type { ApiClients } from '../../../context/ApiClientsContext';
import type { SphereData } from '../../../hooks/useContextData';

export interface TimelineContextAdapterOrganismProps {
  /** API clients from context */
  apiClients: ApiClients | null;
  /** Context view system name to load */
  viewSystemName: string;
  /** Root sphere ID to execute context from */
  rootSphereId: string;
  /** Results per page per level */
  limit?: number;
  /** Max depth for repeat levels */
  depth?: number;
  /** Callback when a timeline item is selected */
  onItemSelect?: (sphereId: string) => void;
  /** Custom render for loading state */
  renderLoading?: () => React.ReactNode;
  /** Custom render for error state */
  renderError?: (error: string) => React.ReactNode;
  /** CSS class name */
  className?: string;
}

interface TimelineItem {
  id: string;
  label: string;
  startDate: Date | null;
  endDate: Date | null;
  group?: string;
  sphere: SphereData;
}

function parseDate(value: unknown): Date | null {
  if (!value) return null;
  const d = new Date(String(typeof value === 'object' ? '' : value));
  return Number.isNaN(d.getTime()) ? null : d;
}

const TimelineContextAdapterOrganism: React.FC<TimelineContextAdapterOrganismProps> = ({
  apiClients,
  viewSystemName,
  rootSphereId,
  limit,
  depth,
  onItemSelect,
  renderLoading,
  renderError,
  className,
}) => {
  const { t } = useTranslation();
  const { view, loading: viewLoading, error: viewError } = useContextView(apiClients, viewSystemName);

  const contextSystemName = view?.context_ref ?? '';

  const {
    data,
    loading: dataLoading,
    error: dataError,
    cacheMetadata,
    refresh,
    fetchMore,
    hasMore,
  } = useContextData(apiClients, {
    systemName: contextSystemName,
    rootSphereId,
    limit,
    depth,
  });

  const loading = viewLoading || dataLoading;
  const error = viewError || dataError;

  const timelineConfig = useMemo(() => {
    if (!view) return null;
    const vc = view.view_config;
    return {
      startDateProperty: (vc.startDateProperty as string) ?? 'startDate',
      endDateProperty: vc.endDateProperty as string | undefined,
      groupBy: vc.groupBy as string | undefined,
      defaultScale: (vc.defaultScale as string) ?? 'month',
      showDependencies: vc.showDependencies as boolean | undefined,
      showProgress: vc.showProgress as boolean | undefined,
      showMilestones: vc.showMilestones as boolean | undefined,
    };
  }, [view]);

  const items: TimelineItem[] = useMemo(() => {
    if (!data || !timelineConfig) return [];
    const allSpheres = Object.values(data.spheresByColumn).flat();
    return allSpheres.map((sphere) => ({
      id: sphere.id,
      label: String(sphere.label ?? sphere.id),
      startDate: parseDate(sphere[timelineConfig.startDateProperty]),
      endDate: timelineConfig.endDateProperty ? parseDate(sphere[timelineConfig.endDateProperty]) : null,
      group: timelineConfig.groupBy ? String((() => { const v = sphere[timelineConfig.groupBy]; return v != null && typeof v !== 'object' ? v : 'Ungrouped'; })()) : undefined,
      sphere,
    }));
  }, [data, timelineConfig]);

  const groups = useMemo(() => {
    if (!timelineConfig?.groupBy) return null;
    const groupSet = new Set<string>();
    for (const item of items) {
      if (item.group) groupSet.add(item.group);
    }
    return Array.from(groupSet).sort((a, b) => a.localeCompare(b));
  }, [items, timelineConfig]);

  if (loading && !data) {
    if (renderLoading) return <>{renderLoading()}</>;
    return <div className={className}>{t('context.common.loadingTimeline')}</div>;
  }

  if (error && !data) {
    if (renderError) return <>{renderError(error)}</>;
    return <div className={className}>{t('context.common.error', { message: error })}</div>;
  }

  if (!data || !view || !timelineConfig) {
    return null;
  }

  const formatDate = (d: Date | null) => d ? d.toLocaleDateString() : '—';

  const renderItems = (filteredItems: TimelineItem[]) =>
    filteredItems.map((item) => (
      <button
        type="button"
        key={item.id}
        className="timeline-context-adapter__item"
        onClick={() => onItemSelect?.(item.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onItemSelect?.(item.id);
        }}
        tabIndex={0}
      >
        <span className="timeline-context-adapter__item-label">{item.label}</span>
        <span className="timeline-context-adapter__item-dates">
          {formatDate(item.startDate)}
          {item.endDate ? ` → ${formatDate(item.endDate)}` : ''}
        </span>
      </button>
    ));

  return (
    <div className={className} data-testid="timeline-context-adapter">
      <div className="timeline-context-adapter__header">
        <span className="timeline-context-adapter__title">
          {view.name?.default ?? view.system_name}
        </span>
        <span className="timeline-context-adapter__scale">
          {t('context.common.scale', { scale: timelineConfig.defaultScale })}
        </span>
        <button
          className="timeline-context-adapter__refresh"
          onClick={refresh}
          title={t('context.common.refreshData')}
          type="button"
        >
          ↻
        </button>
      </div>
      <div className="timeline-context-adapter__content">
        {groups ? (
          groups.map((group) => (
            <div key={group} className="timeline-context-adapter__group">
              <div className="timeline-context-adapter__group-header">{group}</div>
              {renderItems(items.filter((i) => i.group === group))}
            </div>
          ))
        ) : (
          renderItems(items)
        )}
      </div>
      {hasMore && (
        <button
          className="timeline-context-adapter__load-more"
          onClick={fetchMore}
          type="button"
        >
          {t('context.common.loadMore')}
        </button>
      )}
      {cacheMetadata && (
        <div className="timeline-context-adapter__cache-info" data-testid="cache-info">
          {cacheMetadata.structureHit ? t('context.common.cached') : t('context.common.fresh')} · {items.length} {t('context.common.items')}
        </div>
      )}
    </div>
  );
};

export default TimelineContextAdapterOrganism;
